import React from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';

/**
 * AnimatedWrapper Component
 *
 * A reusable wrapper component that adds scroll-triggered animations to its children.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child elements to animate
 * @param {string} props.animation - Animation type (fade-in-up, fade-in-down, fade-in-left, fade-in-right, scale-in)
 * @param {number} props.delay - Animation delay in milliseconds
 * @param {number} props.threshold - Percentage of element visible before triggering (0-1)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.triggerOnce - Whether to trigger animation only once
 *
 * @example
 * <AnimatedWrapper animation="fade-in-up" delay={200}>
 *   <div>Your content here</div>
 * </AnimatedWrapper>
 */
const AnimatedWrapper = ({
  children,
  animation = 'fade-in-up',
  delay = 0,
  threshold = 0.1,
  className = '',
  triggerOnce = true,
}) => {
  const { ref, isVisible } = useScrollAnimation({ threshold, triggerOnce });

  const animationClasses = {
    'fade-in-up': 'animate-fade-in-up',
    'fade-in-down': 'animate-fade-in-down',
    'fade-in-left': 'animate-fade-in-left',
    'fade-in-right': 'animate-fade-in-right',
    'scale-in': 'animate-scale-in',
  };

  const animationClass = animationClasses[animation] || animationClasses['fade-in-up'];

  return (
    <div
      ref={ref}
      className={`${isVisible ? animationClass : 'animate-on-scroll'} ${className} gpu-accelerated`}
      style={{
        animationDelay: isVisible && delay > 0 ? `${delay}ms` : undefined,
        animationFillMode: isVisible ? 'both' : undefined,
      }}
    >
      {children}
    </div>
  );
};

export default AnimatedWrapper;
